//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { configureStore, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions, poolReducer, PoolState } from './poolSlice'

import AlertSlice, { alertActions, AlertState } from './alertSlice'
import navSlice, { NavState } from './navSlice'
import { chainSlice, ChainState, ChainStateInitialized } from './chainSlice'

import { logOT } from '@shared/loggingShared'

import { useDispatch, useSelector, useStore } from 'react-redux'
import { AlertCTO, alertCTOFromError, EthersError, UserError } from '@shared/AlertCTO'
import { useState, useEffect } from 'react'

import { UserConnection } from '@shared/DeploymentConfigs'
import { ethers } from 'ethers'
import { fetchChainTimestampDelta } from '@react/chainThunks/fetchChainTimestampDeltaTx'
import { fetchPerimeterAddresses } from '@react/chainThunks/fetchPerimeterAddressesTx'
import { loginDirectToChainThunk } from '@react/chainThunks/loginToChainThunk'
import { createClientWebsocketHandler } from './wsClient'

export const reducers = {
  poolState: poolReducer,

  alertState: AlertSlice.reducer,
  navState: navSlice.reducer,
  chainState: chainSlice.reducer,
}
export const context = {
  dispatch: (_action: unknown) => {
    logOT.debug('dispatch')
  },
  getState: (): any => {
    logOT.debug('GetState')
    return {}
  },
}
export interface OtStoreState {
  poolState: PoolState

  alertState: AlertState
  navState: NavState
  chainState: ChainState
}

const store = configureStore({
  reducer: reducers,

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),

  // middleware: (getDefaultMiddleware) =>
  //   getDefaultMiddleware(
  //     {
  //       serializableCheck: {
  //         // Ignore these action types
  //         ignoredActions: [web3Actions.web3ConnectGanacheSucceed.toString(), web3Actions.metaMaskConnectSuccess.toString()],
  //         // Ignore these paths in the state
  //         ignoredPaths: ['web3State.web3', 'web3State.web3']
  //       }
  //     }
  //     ).concat(api.middleware).concat(sagaMiddleware).concat(thunk),
})

export async function setupStoreWithInitialStateWithChainConnection(): Promise<OtStore> {
  const store = setupStoreWithInitialState()
  const { chainConfiguration, stageConfiguration } = store.getState().chainState
  console.log('setupStoreWithInitialStateWithChainConnection', chainConfiguration.frontendProviderUrl)
  const providerReadOnly = new ethers.JsonRpcProvider(chainConfiguration.frontendProviderUrl)
  const perimeterAddresses = await fetchPerimeterAddresses(
    chainConfiguration,
    stageConfiguration.serverApiBaseUrl,
    stageConfiguration.restAPIKey
  )
  const chainTimestampDelta = await fetchChainTimestampDelta(providerReadOnly)
  store.dispatch(
    chainSlice.actions.connectChainConfiguration({
      chainConfiguration,
      providerReadOnly,
      chainTimestampDelta,
      perimeterAddresses,
    })
  )

  await createClientWebsocketHandler(
    stageConfiguration.websocketUrl,
    store.dispatch,
    chainConfiguration.chainConfigurationName
  )
  console.log('setupStoreWithInitialStateWithChainConnection3', stageConfiguration.userConnections)
  if (stageConfiguration.userConnections && stageConfiguration.userConnections.length > 0) {
    const USER_STORAGE_KEY = 'directUserAddr'
    const accountAddr = localStorage.getItem(USER_STORAGE_KEY) ?? stageConfiguration.userConnections[0].accountAddr
    if (accountAddr) {
      const userConnection: UserConnection | undefined = stageConfiguration.userConnections.find(
        (uc: UserConnection) => uc.accountAddr === accountAddr
      )

      console.log('setupStoreWithInitialStateWithChainConnection4', userConnection)
      store.dispatch(loginDirectToChainThunk(userConnection as UserConnection))
      let attempts = 0
      const maxAttempts = 50 // 5 seconds total max wait
      const waitForUserConnection = async () => {
        while (attempts < maxAttempts) {
          if (store.getState().chainState.userConnection) {
            console.log('setupStoreWithInitialStateWithChainConnection5', store.getState().chainState.userConnection)
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 100)) // Wait 100ms
          attempts++
        }
      }
      await waitForUserConnection()
    }
  }

  console.log('setupStoreWithInitialStateWithChainConnection2', chainConfiguration.frontendProviderUrl)
  store.dispatch(poolActions.finishedInitializing())

  return store
}
export const setupStoreWithInitialState = (preloadedState?: OtStoreState): OtStore => {
  return configureStore({
    reducer: reducers,
    preloadedState: preloadedState,

    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),

    // middleware: (getDefaultMiddleware) =>
    //   getDefaultMiddleware(
    //     {
    //       serializableCheck: {
    //         // Ignore these action types
    //         ignoredActions: [web3Actions.web3ConnectGanacheSucceed.toString(), web3Actions.metaMaskConnectSuccess.toString()],
    //         // Ignore these paths in the state
    //         ignoredPaths: ['web3State.web3', 'web3State.web3']
    //       }
    //     }
    //     ).concat(api.middleware).concat(sagaMiddleware).concat(thunk),
  })
}

export type OtStore = typeof store

export function assertRole(actualRole: RoleEnum | undefined, expectedRole: RoleEnum | RoleEnum[]) {
  if (!actualRole) {
    throw new Error('Role is not defined')
  }
  if (Array.isArray(expectedRole)) {
    if (!expectedRole.includes(actualRole)) {
      throw new Error(
        `Role ${actualRole} is not allowed to perform this action. Expected one of ${expectedRole.join(', ')}`
      )
    }
  } else {
    if (actualRole !== expectedRole) {
      throw new Error(`Role ${actualRole} is not allowed to perform this action. Expected ${expectedRole}`)
    }
  }
}

//

// Infer the `RootState` and `AppDispatch` types from the store itself

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type OtDispatch = OtStore['dispatch']
export const useOtDispatch = useDispatch.withTypes<OtDispatch>()
export const useOtSelector = useSelector.withTypes<OtStoreState>()

export const useOtChainState = (): ChainStateInitialized => {
  const chainState = useOtSelector((state) => state.chainState)
  if (!chainState.userConnection) {
    return {
      ...chainState,
      userConnection: {} as UserConnection,
    }
  }
  return chainState as ChainState & { userConnection: UserConnection }
}

export const useOtStore: () => OtStore = useStore

export async function dispatchError(
  dispatch: ThunkDispatch<OtStoreState, unknown, UnknownAction>,
  err: EthersError | UserError | Error,
  activity: ActivityEnum,
  errorNumber: number
) {
  let stack: string | undefined = 'no stack'
  if ('stack' in err) {
    stack = err.stack
  } else if ('error' in err && err.error) {
    if ('stack' in err.error) {
      stack = err.error.stack
    }
  }
  logOT.info('handleError', errorNumber, activity, stack)
  const alertCTO: AlertCTO = alertCTOFromError(err, activity)
  alertCTO.errorNumber = errorNumber
  dispatch(alertActions.addAlert(alertCTO))
  logOT.error('Error:', alertCTO)
}

export const useOtChainTimestamp = () => {
  const [currentTimestamp, setCurrentTimestamp] = useState<number>(Math.round(Date.now() / 1000))
  const {
    chainConfiguration: { chainTimestampDelta },
  } = useOtChainState()
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTimestamp(Math.round(Date.now() / 1000))
    }, 60000) // Update every 60 second to keep current timestamp fresh

    return () => clearInterval(intervalId) // Clean up interval on unmount
  }, [])

  // Return the sum of the current timestamp and chainstate.chaintimestamp
  return currentTimestamp + chainTimestampDelta
}
